import type { Process } from '@gotti/core';
import type { AreaServer } from '@gotti/server';
import type { MockEnvironment } from '@gotti/test-utils';
import type { PlaygroundGameContext, PlaygroundRuntimeSelection } from '../types.js';
import { forEachClientNowAndLater } from './clients.js';

const PROCESS_HOOK_STATE = new WeakMap<Process, { active: boolean }>();
const ENVIRONMENT_HOOKED = Symbol('gotti.playground.environmentHooked');

const safeInvoke = (label: string, cb?: () => void) => {
  if (!cb) return;
  try {
    cb();
  } catch (err) {
    console.error(`[gotti][playground] error in ${label} gameHooks`, err);
  }
};

const attachProcessHooks = <TTarget>(
  process: (Process & { update: (delta: number) => void }) | undefined,
  target: TTarget,
  hooks: { onStart: (api: any, target: TTarget, ctx: PlaygroundGameContext) => void; onUpdate?: (api: any, target: TTarget, ctx: PlaygroundGameContext) => void; onStop?: (api: any, target: TTarget, ctx: PlaygroundGameContext) => void; },
  ctx: PlaygroundGameContext,
  label: string
) => {
  if (!process || PROCESS_HOOK_STATE.has(process)) {
    return;
  }

  PROCESS_HOOK_STATE.set(process, { active: false });
  const state = PROCESS_HOOK_STATE.get(process)!;

  const runStart = () => {
    if (state.active) return;
    state.active = true;
    safeInvoke(label, () => hooks.onStart(process.$api, target, ctx));
  };

  const runStop = () => {
    if (!state.active) return;
    state.active = false;
    safeInvoke(label, () => hooks.onStop?.(process.$api, target, ctx));
  };

  const originalStartLoop = process.startLoop?.bind(process);
  if (originalStartLoop) {
    process.startLoop = (...args: any[]) => {
      const result = originalStartLoop(...args);
      runStart();
      return result;
    };
  }

  const originalStopLoop = process.stopLoop?.bind(process);
  if (originalStopLoop) {
    process.stopLoop = (...args: any[]) => {
      // runStop never throws — a failing onStop hook is logged (safeInvoke) — so the process's own stop always runs after it.
      runStop();
      return originalStopLoop(...args);
    };
  }

  if (hooks.onUpdate) {
    const originalUpdate = process.update.bind(process);
    process.update = (delta: number) => {
      const result = originalUpdate(delta);
      safeInvoke(label, () => hooks.onUpdate?.(process.$api, target, ctx));
      return result;
    };
  }

  if (process.startedLoop) {
    runStart();
  }
};

const buildContext = (
  selection: PlaygroundRuntimeSelection,
  environment: MockEnvironment
): PlaygroundGameContext => {
  return {
    areas: Object.values(environment.areas ?? {}) as AreaServer[],
    gate: selection.environment?.gate as any,
    master: selection.environment?.master as any,
    clients: environment.clients,
  };
};

export const applyPlaygroundGameHooks = (
  selection: PlaygroundRuntimeSelection,
  environment: MockEnvironment | null | undefined
): void => {
  if (!environment || !selection.gameHooks) {
    return;
  }

  const env = environment;

  if ((env as any)[ENVIRONMENT_HOOKED]) {
    return;
  }
  (env as any)[ENVIRONMENT_HOOKED] = true;

  const ctx = buildContext(selection, env);
  const { server, client } = selection.gameHooks;

  if (server) {
    const areas = Object.values(env.areas ?? {});
    areas.forEach((area) => {
      attachProcessHooks(area.gameProcess, area, server, ctx, 'server');
    });
  }

  if (client) {
    forEachClientNowAndLater(env, (mockClient) => {
      attachProcessHooks(mockClient.process, mockClient, client, ctx, 'client');
    });
  }
};
